import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkle, DownloadSimple, Translate } from "@phosphor-icons/react";
import DocumentPreview, { downloadDocx } from "@/components/app/DocumentPreview";

const fetcher = (u) => api.get(u).then((r) => r.data);

async function downloadPdf(id, title, lang) {
  try {
    const r = await api.get(`/deliverables/${id}/export-pdf?lang=${lang}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^\w\-]+/g, "_")}_${lang.toUpperCase()}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error("Export PDF impossible"); }
}

function translateLabel(selected, translating) {
  if (selected.translation_status === "en_generation") return "Traduction en cours…";
  if (selected.content_en) return "Voir FR/EN";
  if (translating) return "Traduction…";
  return "Traduire en anglais";
}

function TranslateTabContent({ selected, testidPrefix }) {
  if (selected.translation_status === "en_generation") {
    return (
      <div className="p-12 text-center text-sm text-zinc-500 border border-zinc-200 rounded-md bg-white" data-testid={`${testidPrefix}-translating`}>
        <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mr-2 align-middle"></span>
        Traduction en cours…
      </div>
    );
  }
  if (selected.translation_status === "erreur") {
    return (
      <div className="border border-red-200 bg-red-50 rounded-md p-6 text-sm text-red-800">
        {selected.translation_error || "La traduction a échoué. Merci de réessayer."}
      </div>
    );
  }
  return <DocumentPreview content={selected.content_en || ""} />;
}

function DeliverableBody({ selected, testidPrefix }) {
  if (selected.status === "en_generation") {
    return (
      <div className="border border-zinc-200 bg-white rounded-md p-12 text-center" data-testid={`${testidPrefix}-generating`}>
        <div className="inline-block w-5 h-5 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mb-3"></div>
        <div className="text-sm text-zinc-600">Rédaction IA en cours — le document apparaîtra automatiquement (30 à 90 secondes)…</div>
      </div>
    );
  }
  if (selected.status === "erreur") {
    return (
      <div className="border border-red-200 bg-red-50 rounded-md p-6 text-sm text-red-800" data-testid={`${testidPrefix}-error`}>
        {selected.error_message || "La génération a échoué. Merci de réessayer."}
      </div>
    );
  }
  const enDisabled = !selected.content_en && selected.translation_status !== "en_generation";
  return (
    <Tabs defaultValue="fr">
      <TabsList>
        <TabsTrigger value="fr">Français</TabsTrigger>
        <TabsTrigger value="en" disabled={enDisabled} data-testid={`${testidPrefix}-tab-en`}>English</TabsTrigger>
      </TabsList>
      <TabsContent value="fr"><DocumentPreview content={selected.content_fr} /></TabsContent>
      <TabsContent value="en"><TranslateTabContent selected={selected} testidPrefix={testidPrefix} /></TabsContent>
    </Tabs>
  );
}

function DeliverableHistory({ items, selected, onSelect, titleFn, subtitleFn, emptyText, testidPrefix }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <div className="p-4 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Historique</div>
      <ul className="divide-y divide-zinc-100 max-h-[70vh] overflow-y-auto">
        {items.length === 0 && <li className="p-6 text-sm text-zinc-500">{emptyText}</li>}
        {items.map((it) => (
          <li key={it.id}>
            <button
              data-testid={`${testidPrefix}-item-${it.id}`}
              onClick={() => onSelect(it)}
              className={`w-full text-left p-4 hover:bg-zinc-50 ${selected?.id === it.id ? "bg-zinc-50" : ""}`}
            >
              <div className="text-sm font-medium truncate">{titleFn(it)}</div>
              <div className="text-xs text-zinc-500 truncate">{subtitleFn(it)}</div>
              <div className="text-[11px] text-zinc-400 mt-1">{new Date(it.created_at).toLocaleString("fr-FR")}</div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeliverableDetail({ selected, onTranslate, translating, eyebrow, titleFn, emptyHint, testidPrefix }) {
  if (!selected) {
    return <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center text-zinc-500 text-sm">{emptyHint}</div>;
  }
  const translatingNow = translating || selected.translation_status === "en_generation";
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{eyebrow}</div>
          <h2 className="font-serif-display text-2xl">{titleFn(selected)}</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onTranslate} disabled={translatingNow || selected.status === "en_generation"} data-testid={`${testidPrefix}-translate`}>
            <Translate size={16} className="mr-1" /> {translateLabel(selected, translating)}
          </Button>
          <Button variant="outline" onClick={() => downloadDocx(selected.title, selected.content_fr)} disabled={!selected.content_fr} data-testid={`${testidPrefix}-download-fr`}>
            <DownloadSimple size={16} className="mr-1" /> Word (FR)
          </Button>
          <Button variant="outline" onClick={() => downloadPdf(selected.id, selected.title, "fr")} disabled={!selected.content_fr} data-testid={`${testidPrefix}-pdf-fr`}>
            <DownloadSimple size={16} className="mr-1" /> PDF (FR)
          </Button>
          {selected.content_en && (
            <>
              <Button variant="outline" onClick={() => downloadDocx(`${selected.title}_EN`, selected.content_en)} data-testid={`${testidPrefix}-download-en`}>
                <DownloadSimple size={16} className="mr-1" /> Word (EN)
              </Button>
              <Button variant="outline" onClick={() => downloadPdf(selected.id, selected.title, "en")} data-testid={`${testidPrefix}-pdf-en`}>
                <DownloadSimple size={16} className="mr-1" /> PDF (EN)
              </Button>
            </>
          )}
        </div>
      </div>
      <DeliverableBody selected={selected} testidPrefix={testidPrefix} />
    </div>
  );
}

/**
 * Studio commun Lettres de mission / Programmes de travail.
 * Gère : historique, génération IA asynchrone (polling), traduction FR→EN, export Word.
 */
export default function DeliverableStudio({
  kind, generateEndpoint, eyebrow, title, description, dialogTitle,
  generateButtonLabel, generateTestid, initialForm, renderFormFields,
  buildPayload, canSubmit, historyTitleFn, historySubtitleFn, detailTitleFn,
  detailEyebrow, emptyText, emptyHint, testidPrefix,
}) {
  const listKey = `/deliverables?kind=${kind}`;
  const { data: items = [] } = useSWR(listKey, fetcher);
  const { data: clients = [] } = useSWR("/clients", fetcher);
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [generating, setGenerating] = React.useState(false);
  const [translating, setTranslating] = React.useState(false);
  const [form, setForm] = React.useState(initialForm);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const r = await api.post(generateEndpoint, buildPayload(form));
      toast.success("Génération lancée — le document apparaîtra automatiquement");
      setOpen(false);
      setSelected(r.data);
      mutate(listKey);
    } catch (err) { toast.error(err?.response?.data?.detail || "Génération impossible"); }
    finally { setGenerating(false); }
  };

  const pollKey = selected && (selected.status === "en_generation" || selected.translation_status === "en_generation")
    ? `/deliverables/${selected.id}` : null;
  useSWR(pollKey, fetcher, { refreshInterval: 2500, onSuccess: (d) => setSelected(d) });

  const translate = async () => {
    if (!selected) return;
    setTranslating(true);
    try {
      const r = await api.post(`/deliverables/${selected.id}/translate`);
      setSelected(r.data);
      toast.success("Traduction lancée");
    } catch (err) { toast.error(err?.response?.data?.detail || "Traduction impossible"); }
    finally { setTranslating(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={generateTestid} className="bg-[#2E3192] hover:bg-[#1E2066]">
                <Sparkle size={16} className="mr-1" /> {generateButtonLabel}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-serif-display text-2xl">{dialogTitle}</DialogTitle></DialogHeader>
              <form onSubmit={generate} className="space-y-4">
                {renderFormFields(form, setForm, clients)}
                <div className="flex justify-end">
                  <Button type="submit" disabled={generating || !canSubmit(form)} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid={`${testidPrefix}-submit`}>
                    {generating ? "Rédaction en cours…" : "Générer avec IA"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DeliverableHistory
          items={items}
          selected={selected}
          onSelect={setSelected}
          titleFn={(it) => historyTitleFn(it, clientMap)}
          subtitleFn={(it) => historySubtitleFn(it, clientMap)}
          emptyText={emptyText}
          testidPrefix={testidPrefix}
        />
        <div className="lg:col-span-2">
          <DeliverableDetail
            selected={selected}
            onTranslate={translate}
            translating={translating}
            eyebrow={detailEyebrow}
            titleFn={(s) => detailTitleFn(s, clientMap)}
            emptyHint={emptyHint}
            testidPrefix={testidPrefix}
          />
        </div>
      </div>
    </div>
  );
}
